<template>
  <div class="flex items-start gap-2">
    <span
      v-if="directFlightsStore.isRoundTrip"
      class="text-text-neutral-quaternary min-w-16 whitespace-nowrap text-sm"
    >
      {{ directionLabel }}
    </span>
    <div
      ref="containerRef"
      class="mr-8 flex w-full items-center gap-2 overflow-hidden"
    >
      <span
        v-for="(time, timeIdx) in visibleTimes"
        :key="timeIdx"
        class="text-text-neutral-primary whitespace-nowrap text-sm"
      >
        {{ time }}
      </span>
      <span
        v-if="truncatedCount > 0"
        class="text-text-neutral-tertiary bg-situational-light whitespace-nowrap rounded-2xl px-2 py-1 text-xs font-medium"
      >
        {{ $t('search.directFlights.more', { count: truncatedCount }) }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { useDirectFlightsStore } from '../model';
  import { calculateVisibleTimeItems } from '../lib';
  import type { DayObject } from '../types';

  const props = defineProps<{
    day: DayObject;
    dayIndex?: number;
  }>();

  const { t } = useI18n();

  const directFlightsStore = useDirectFlightsStore();

  const containerRef = ref<HTMLElement | null>(null);
  const visibleCount = ref<number>(props.day.times.length);

  const directionLabel = computed(() => {
    if (directFlightsStore.isRoundTrip && props.dayIndex !== undefined) {
      // First day (index 0) is forward, second day (index 1) is backward
      return props.dayIndex === 0
        ? t('main.dates.placeholder.departure.short')
        : t('main.dates.placeholder.arrival.short');
    }
    return '';
  });

  const visibleTimes = computed(() => {
    return props.day.times.slice(0, visibleCount.value);
  });

  const truncatedCount = computed(() => {
    return Math.max(0, props.day.times.length - visibleCount.value);
  });

  const calculateVisibleItems = () => {
    if (!containerRef.value) {
      visibleCount.value = props.day.times.length;
      return;
    }

    const { offsetWidth: containerWidth } = containerRef.value;

    visibleCount.value = calculateVisibleTimeItems(
      containerWidth,
      props.day.times,
      (count: number) => t('search.directFlights.more', { count }),
    );
  };

  onMounted(() => {
    calculateVisibleItems();
  });

  watch(
    () => props.day.times,
    () => {
      calculateVisibleItems();
    },
  );
</script>
